import { useMemo } from "react";
import jwtDecode from "jwt-decode";
import { msalInstance } from "../App";
interface DecodedToken {
  roles?: string[];
  aud?: string;
  iss?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  name?: string;
  preferred_username?: string;
  [key: string]: any;
}

/**
 * Get user roles from MSAL ID token claims
 * Falls back to access token from localStorage if MSAL is not available
 */
export const getUserRoles = (): string[] => {
  try {
    // Try to get roles from MSAL ID token claims (preferred method)
    if (msalInstance) {
      const account = msalInstance.getActiveAccount();
      if (account?.idTokenClaims) {
        const claims = account.idTokenClaims as any;
        // Roles can be in 'roles' or 'role' claim
        const roles = claims.roles || claims.role || [];
        const rolesArray = Array.isArray(roles) ? roles : [roles];
        return rolesArray;
      }
    }

    // Fallback to decoding access token from localStorage
    const token = localStorage.getItem("accessToken");
    if (!token) {
      return [];
    }

    const decoded: DecodedToken = jwtDecode(token);
    const roles = decoded.roles || [];
    return roles;
  } catch (error) {
    console.error("Error getting user roles:", error);
    return [];
  }
};

export const hasRole = (role: string): boolean => {
  const roles = getUserRoles();
  return roles.includes(role);
};

export const hasAnyRole = (allowedRoles: string[]): boolean => {
  const roles = getUserRoles();
  return allowedRoles.some((role) => roles.includes(role));
};

/** True when the signed-in user has the Admin app role (not Developer). */
export const isAdmin = (): boolean => hasRole("Admin");

// Roles cached per signed-in account for the page's lifetime: sign-in and sign-out
// are full-page redirects, so the roles cannot change underneath a loaded page.
let cachedRoles: { accountId: string; roles: string[] } | null = null;

const getSessionRoles = (): string[] => {
  const accountId = msalInstance?.getActiveAccount()?.homeAccountId ?? "";
  if (cachedRoles && accountId && cachedRoles.accountId === accountId) {
    return cachedRoles.roles;
  }
  const roles = getUserRoles();
  // Only cache once an account is known, so a pre-sign-in empty answer never sticks.
  if (accountId) cachedRoles = { accountId, roles };
  return roles;
};

/**
 * The signed-in user's app roles (e.g. ["Admin"]), read once per session instead of
 * decoding the token on every render. Returns a stable array.
 */
export const useRoles = (): string[] => useMemo(() => getSessionRoles(), []);
